import { LoginOutlined } from '@ant-design/icons';
import { Switch } from 'antd';
import { AccountBean, AccountListItemBean, ListResponseAccountListItemBean } from 'common/api/api';
import { accountApi } from 'common/api/entitiesApi';
import { ActiveStatusSelector } from 'common/components/dictionary/ActiveStatusSelector.tsx';
import { RoleSelector } from 'common/components/dictionary/RoleSelector';
import { ListCard } from 'common/components/ListCard';
import { AdvertiserSelector } from 'common/components/reloadable/AdvertiserSelector.tsx';
import { AgencySelector } from 'common/components/reloadable/AgencySelector.tsx';
import { SearchInput } from 'common/components/SearchInput';
import { ListFilters, useAppContext } from 'common/context/AppContext';
import { COLUMN_ID_WIDTH, COLUMN_NAME_WIDTH } from 'common/util/constants.ts';
import { initListFilters, useListFilters } from 'common/util/useListFilters';
import { apiCall, showError } from 'common/util/Util';
import dayjs from 'dayjs';
import * as React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import RoleEnum = AccountBean.RoleEnum
import { ListActionButton } from 'common/components/ListActionButton.tsx'

const AccountList: React.FC<{ admin?: boolean }> = ({ admin }) => {
  const appContext = useAppContext()
  const [filters, setFilters] = useState<ListFilters>(initListFilters())
  const { setFilter } = useListFilters(setFilters)

  const [loading, setLoading] = useState(false)
  const [listData, setListData] = useState<ListResponseAccountListItemBean | undefined>()

  function loginAs(id: number) {
    accountApi
      .loginAs(id)
      .then((response) => {
        window.open(response.data, '_blank')
      })
      .catch((e) => showError(appContext, e))
  }

  const loadData = () => {
    apiCall(
      setLoading,
      appContext,
      () =>
        accountApi.accountList(
          filters.sort?.name,
          filters.sort?.direction,
          filters.name,
          filters.active,
          filters.role,
          filters.agencyId,
          filters.advertiserId,
          { query: { page: filters.page, size: filters.pageSize } },
        ),
      (data) => setListData(data),
    )
  }

  const toggleActive = (record: AccountBean, checked: boolean) => {
    if (record.id) {
      const id = record.id
      apiCall(
        setLoading,
        appContext,
        () => accountApi.changeAccountStatus(id),
        () => {
          record.active = checked
          setListData(listData)
        },
      )
    }
  }

  return (
    <ListCard
      loading={loading}
      title="Accounts"
      createName="Account"
      createRoute="/account/create"
      listRouteKey="/account"
      filters={filters}
      setFilters={setFilters}
      listData={listData}
      loadData={loadData}
      columns={[
        {
          title: 'ID',
          dataIndex: 'id',
          width: COLUMN_ID_WIDTH,
          fixed: 'left'
        },
        {
          title: 'Name',
          dataIndex: 'name',
          className: 'list-table-name',
          sorter: true,
          ellipsis: true,
          width: COLUMN_NAME_WIDTH,
          fixed: 'left',
          render: (text: string, record: AccountBean) => (
            <Link style={{ color: '#5888CF' }} to={`/account/${record.id}/edit`}>
              {text}
            </Link>
          ),
        },
        {
          title: 'Email',
          dataIndex: 'email',
          width: 200,
          ellipsis: true,
        },
        {
          title: 'Role',
          dataIndex: 'role',
          sorter: true,
          width: 120,
        },
        {
          title: 'Agency',
          dataIndex: 'agencyName',
          hidden: !admin,
          width: 120,
          render: (_: any, record: AccountListItemBean) =>
            record.agencyId !== undefined ? (
              <Link style={{ color: '#5888CF' }} to={`/agency/${record.agencyId}/edit`}>
                {record.agencyName}
              </Link>
            ) : (
              '-'
            ),
        },
        {
          title: 'Advertiser',
          dataIndex: 'advertiserName',
          width: 120,
          render: (_: any, record: AccountBean) =>
            record.advertiserId !== undefined ? (
              admin ? (
                record.advertiserName
              ) : (
                <Link style={{ color: '#5888CF' }} to={`/adv/${record.advertiserId}/edit`}>
                  {record.advertiserName}
                </Link>
              )
            ) : (
              '-'
            ),
        },
        {
          title: 'Last Modified',
          dataIndex: 'modified',
          sorter: true,
          width: 100,
          render: (date) => date && dayjs(date).format('DD MMM YYYY'),
        },
        {
          title: 'Active',
          dataIndex: 'active',
          sorter: true,
          render: (val, record: AccountBean) => (
            <Switch size="small" checked={record.active} onChange={(checked) => record.id && toggleActive(record, checked)} />
          ),
          width: 60,
        },
        {
          title: 'Actions',
          width: 100,
          render: (_: any, record: AccountBean) => (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', height: 32 }}>
              {(record.role === RoleEnum.Advertiser || (admin && record.role === RoleEnum.Admin)) && (
                <>
                  <ListActionButton
                    onClick={() => loginAs(record.id as number)}
                    tooltip='Login As'
                  >
                    <LoginOutlined />
                  </ListActionButton>
                </>
              )}
            </div>
          ),
        },
      ]}
    >
      <SearchInput
        value={filters.name}
        onChange={(val) => setFilter('name', val)}
        placeholder="Search by ID, Name, Email"
        width="250px"
      />
      <AgencySelector
        placeholder="Filter by Agency"
        style={{ minWidth: '200px' }}
        value={filters.agencyId}
        onChange={(value: number) => setFilter('agencyId', value)}
      />
      <RoleSelector
        allowSuperAdmin={admin}
        placeholder="Filter by Role"
        style={{ width: '130px' }}
        value={filters.role}
        onChange={(value: string) => setFilter('role', value)}
      />
      <AdvertiserSelector
        placeholder="Filter by Advertiser"
        style={{ minWidth: '200px' }}
        value={filters.advertiserId}
        onChange={(value: number) => setFilter('advertiserId', value)}
        disabled={filters.role !== RoleEnum.Advertiser.toString()}
      />
      <ActiveStatusSelector
        placeholder="Filter by Active"
        style={{ minWidth: '100px' }}
        value={filters.active}
        onChange={(value) => setFilter('active', value)}
      />
    </ListCard>
  )
}

export default AccountList
